import React from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import {
  Avatar,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import {
  FiCompass,
  FiSearch,
  FiMessageCircle,
  FiBookmark,
  FiLogOut,
  FiChevronDown,
} from "react-icons/fi";
import { ColorModeSwitcher } from "./ColorModeSwitcher";
import { useSession } from "./session";
const links = [
  { to: "/", label: "Discover", icon: FiCompass },
  { to: "/search", label: "Search", icon: FiSearch },
  { to: "/chats", label: "Chats", icon: FiMessageCircle },
  { to: "/saved", label: "Saved", icon: FiBookmark },
];
function Navigation({ mobile = false }) {
  return (
    <nav
      aria-label={mobile ? "Mobile navigation" : "Main navigation"}
      className={mobile ? "mobile-nav" : "main-nav"}
    >
      {links.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/"}
          className={({ isActive }) =>
            isActive ? "nav-item active" : "nav-item"
          }
        >
          <Icon aria-hidden="true" />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}
export default function AppShell({ children }) {
  const { auth, recent } = useSession(),
    location = useLocation(),
    chat = location.pathname.startsWith("/chat/");
  return (
    <div className={`app-shell${chat ? " is-chat" : ""}`}>
      <aside className="sidebar">
        <Link to="/" className="brand-logo" aria-label="Chararo home">
          <span className="brand-mark">
            <FiMessageCircle />
          </span>
          Chararo
        </Link>
        <Navigation />
        {auth.isAuthenticated && recent.length > 0 && (
          <div className="recent-sidebar">
            <p className="eyebrow">RECENT CHATS</p>
            {recent.map((c) => (
              <Link
                key={c.id}
                to={`/chat/${c.character.publicId}?conversation=${c.id}`}
                className="recent-link"
              >
                <Avatar
                  src={c.character.thumb}
                  name={c.character.name}
                  size="sm"
                />
                <span>
                  <strong>{c.character.name}</strong>
                  <small>{c.title}</small>
                </span>
              </Link>
            ))}
          </div>
        )}
        <div className="sidebar-bottom">
          <div className="theme-row">
            <span>Appearance</span>
            <ColorModeSwitcher />
          </div>
          {auth.isAuthenticated ? (
            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                width="100%"
                height="auto"
                py={3}
                rightIcon={<FiChevronDown />}
              >
                <span className="account-name">
                  <Avatar
                    size="sm"
                    src={auth.user?.picture}
                    name={auth.user?.given_name || "You"}
                  />
                  <span>{auth.user?.given_name || "Your account"}</span>
                </span>
              </MenuButton>
              <MenuList>
                <MenuItem icon={<FiLogOut />} onClick={() => auth.logout()}>
                  Log out
                </MenuItem>
              </MenuList>
            </Menu>
          ) : (
            <>
              <Button
                variant="brand"
                width="full"
                isLoading={auth.isLoading}
                onClick={auth.login}
              >
                Log in
              </Button>
              <button
                className="text-link register-link"
                onClick={auth.register}
              >
                Create an account
              </button>
            </>
          )}
          <div className="legal-links">
            <Link to="/terms-of-use">Terms</Link>
            <Link to="/privacy-policy">Privacy</Link>
          </div>
        </div>
      </aside>
      {!chat && (
        <header className="mobile-header">
          <Link to="/" className="brand-logo">
            <span className="brand-mark">
              <FiMessageCircle />
            </span>
            Chararo
          </Link>
          <div className="inline-actions">
            <ColorModeSwitcher />
            {auth.isAuthenticated ? (
              <IconButton
                aria-label="Log out"
                variant="ghost"
                icon={<FiLogOut />}
                onClick={() => auth.logout()}
              />
            ) : (
              <Button
                size="sm"
                variant="outline"
                isLoading={auth.isLoading}
                onClick={auth.login}
              >
                Log in
              </Button>
            )}
          </div>
        </header>
      )}
      <main className="app-main">{children}</main>
      {!chat && <Navigation mobile />}
    </div>
  );
}
